import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                       NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *           UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 *     This program is confidential and a trade secret of Ab Initio.
 *     The receipt or possession of this program does not convey any
 *     rights to reproduce or disclose its contents, or to manufacture,
 *     use or sell anything that it may describe, in whole or in part,
 *     without the specific written consent of Ab Initio.
 */

import { AbPortalUserNavigationService } from "abportal/service/AbPortalUserNavigationService";
import { LocalPersonaService } from "abweb/service/persona/LocalPersonaService";
import { ExtensionPoints } from "abweb/utils/plugins/ExtensionPoints";
import * as React from "react";

/**
 * The base interface that all Portal applications must provide.
 */

/**
 * A hosted application can provide the entire page layout, for full flexibility.
 */

/**
 * A custom application uses the Portal's layout, but can optionally customize
 * the banner and side navigation.
 */

/**
 * Initial version of what it means to define an application within the portal.
 * For now this is used to power a side navigation bar (with application switcher)
 * and display the application name in the banner
 * */

/**
 * Initial version of what it means to define a space within the portal. These will be added to the side navigation for the "main" portal application
 * */

/**
 * An object that manages the visibility of spaces for the current user.
 */

export const PortalApplicationContext = /*#__PURE__*/React.createContext(undefined);
PortalApplicationContext.displayName = "PortalApplicationContext";

/**
 * Extension points to integrate with the Ab Initio Portal.
 */
export class AbPortalExtensions {}
/**
 * An event dispatcher to receive a route registry.
 * <p>
 * This is how an Ab Initio Portal application registers its routes.
 * </p>
 */
_defineProperty(AbPortalExtensions, "PORTAL_ROUTES_LOAD", ExtensionPoints.eventDispatcher());
_defineProperty(AbPortalExtensions, "PORTAL_DEFAULT_ROUTE", ExtensionPoints.singleValue());
/**
 * An extension point to register localization resources for the Ab Initio Portal UI to load.
 * <p>
 * Add your localization resource string, e.g. <pre>webapp/app/assets/locale/{locale}/<i>myresources</i>.json</pre>.
 * </p>
 * */
_defineProperty(AbPortalExtensions, "PORTAL_LOCALES", ExtensionPoints.multiValue());
/** An extension point to register additional style sheet imports. */
_defineProperty(AbPortalExtensions, "PORTAL_STYLE_SHEET_LOADER", ExtensionPoints.eventDispatcher());
/** An extension point to register any additional image registries for the portal to load. */
_defineProperty(AbPortalExtensions, "PORTAL_IMAGE_REGISTRIES", ExtensionPoints.multiValue());
/** Extension point to register any Metadata Hub extension set initializers into the portal load sequence. */
_defineProperty(AbPortalExtensions, "PORTAL_EXTENSION_SET_INITIALIZERS", ExtensionPoints.multiValue());
/** register the concept of an application with the portal  */
_defineProperty(AbPortalExtensions, "PORTAL_APPLICATIONS", ExtensionPoints.syncKeyedValues(portalApplication => portalApplication.id));
/** register the concept of a space with the portal  */
_defineProperty(AbPortalExtensions, "PORTAL_SPACES", ExtensionPoints.syncKeyedValues(portalSpace => portalSpace.id));
_defineProperty(AbPortalExtensions, "PORTAL_UTILITY_PANELS", ExtensionPoints.syncKeyedValues(utilityPanel => utilityPanel.id));
// register which utility panel (if any) should open up when the user first visits.
_defineProperty(AbPortalExtensions, "PORTAL_UTILITY_PANEL_INITIALLY_OPEN", ExtensionPoints.syncSingleValue(() => undefined));
/** Extension point that provides the PersonaService for the portal. */
_defineProperty(AbPortalExtensions, "PORTAL_PERSONA_SERVICE", ExtensionPoints.singleValue(async () => new LocalPersonaService()));
/** Extension point that provides the UserNavigationService for the portal. */
_defineProperty(AbPortalExtensions, "PORTAL_USER_NAVIGATION_SERVICE", ExtensionPoints.singleValue(async () => new AbPortalUserNavigationService()));
/** Extension point that provides help topic that the built-in "Data Platform" AbPortalApplication uses */
_defineProperty(AbPortalExtensions, "PORTAL_HELP_TOPIC", ExtensionPoints.syncSingleValue(() => undefined));
/** Extension point that provides what's new help topic that the built-in "Data Platform" AbPortalApplication uses */
_defineProperty(AbPortalExtensions, "PORTAL_WHATS_NEW_HELP_TOPIC", ExtensionPoints.syncSingleValue(() => undefined));
/*
 * These four extension points are not intended for general use.
 * They just allow the legacy VDL plugin to wire into the lifecycle.
 */
/** Run during initial bootstrapping of the UI. */
_defineProperty(AbPortalExtensions, "PORTAL_BOOTSTRAPPERS", ExtensionPoints.eventDispatcher());
/** Run just after service initialization, to allow a plugin to register their own services. */
_defineProperty(AbPortalExtensions, "PORTAL_SERVICE_INITIALIZERS", ExtensionPoints.eventDispatcher());
/**
 * See AIC-966 - we need to render this for bulk editing to work in EntityQueryGrids
 * NOTE: The Portal team would like to discourage this pattern of adding a core extension point for a specific feature
 * and therefore please don't copy this pattern going forward.
 */
_defineProperty(AbPortalExtensions, "CLIENT_FUNC_EXECUTOR_RENDERER", ExtensionPoints.singleValue(
//by providing a default value here, we prevent errors from being thrown when it isn't registered such as in AIC-1357
() => Promise.resolve({
  renderStub: () => undefined
})));
//# sourceMappingURL=AbPortalPlugin.js.map
